import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const endpoint = '/UserAccount/RegistrationQueueRequests';

export default function RegistrationQueuePage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const { get, response } = useFetch(endpoint);
  const { post: cancelRequest, response: cancelResponse } = useFetch(endpoint);

  const fetchData = async () => {
    setLoading(true);
    const result = await get();
    setData(response.ok && Array.isArray(result) ? result : []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const cancel = async () => {
    if (!selected) return;

    setLoading(true);
    await cancelRequest(`/${selected.id}/Cancel`);
    if (cancelResponse.ok) {
      setSelected(null);
      await fetchData();
    } else {
      setLoading(false);
    }
  };

  return (
    <>
      <DataTable
        title="Registracijos į eilę"
        columns={columns(setSelected)}
        data={data}
        loading={loading}
        filterableColumns={['service', 'department', 'status']}
      />

      <AlertDialog
        open={!!selected}
        onOpenChange={(open) => !open && setSelected(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Atšaukti registraciją į eilę?</AlertDialogTitle>
            <AlertDialogDescription>
              Atšaukus šią registraciją, ji bus pažymėta kaip atšaukta.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>
              Grįžti
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600"
              onClick={cancel}
              disabled={loading}
            >
              Atšaukti
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const columns = (setSelected) => [
  {
    accessorKey: 'service',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="PASLAUGA" />
    ),
    cell: ({ row }) => (
      <div className="font-medium leading-tight">
        <div>{row.original.service}</div>
        {row.original.specialist && (
          <div className="text-muted-foreground text-sm">
            {row.original.specialist}
          </div>
        )}
      </div>
    ),
  },
  {
    accessorKey: 'department',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="PADALINYS" />
    ),
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="SUKŪRIMO DATA" />
    ),
    cell: ({ row }) => format(new Date(row.original.createdAt), 'yyyy-MM-dd'),
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="BŪSENA" />
    ),
    cell: ({ row }) => (
      <Badge variant={row.original.canCancel ? 'success' : 'neutral'}>
        {row.original.status}
      </Badge>
    ),
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) =>
      row.original.canCancel ? (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              setSelected(row.original);
            }}
          >
            Atšaukti
          </Button>
        </div>
      ) : null,
  },
];
